import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { cn } from '../../lib/utils';
import { Badge, Button } from '../../components/ui';
import { AccordionItem } from '../../components/ui/Accordion';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  DocumentTextIcon,
  PencilIcon,
  PrinterIcon,
} from '../../components/ui/Icons';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { formatDate as formatDateGlobal, formatDateTime } from '../../config/appConfig';
import {
  generateMgaAiOverview,
  getMgaAiOverview,
  getMgaAiOverviewHistory,
  type MgaAiOverviewResponse,
} from '../../services/mutations';
import {
  formatMgaStatus,
  moduleConfig,
  statusBadgeVariant,
} from './config';
import { closeApprovalBadgeVariant, CloseApprovalStatusCard } from './CloseApprovalStatusCard';
import {
  useMgaAuditEvents,
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaCrewRequests,
  useMgaPeriod,
} from './useData';
import { useAccDocument } from '../accounting/documents/useData';
import { DocumentLinesView } from '../accounting/shared/DocumentLinesView';
import {
  CASH_INCOME_CODE,
  BONDED_INCOMING_CODE,
  requestColumnCode,
} from './booksCategories';
import { carryForwardSummary } from './closeChecklist';
import { PeriodFilesPanel } from './PeriodFilesPanel';
import { MGA_REPORT_KEYS, MGA_REPORT_META } from './reports/reportMeta';
function formatAmt(n: number | null | undefined, currency = 'USD') {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 2,
  }).format(Number(n));
}

function auditEntityLabel(entityType: string, summary: string, action: string) {
  const s = summary.toLowerCase();
  if (entityType === 'DOCUMENT') {
    if (s.includes('accdocument') || s.includes('journal') || action === 'POST') return 'Journal';
    return 'File';
  }
  if (entityType === 'PERIOD') return 'Period';
  if (entityType === 'LINE' || entityType === 'CREW_LINE') return 'Payroll';
  if (entityType === 'CASH') return 'Cash';
  if (entityType === 'BONDED') return 'Bonded';
  if (entityType === 'REQUEST') return 'Books';
  return entityType
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function auditActionLabel(action: string) {
  const map: Record<string, string> = {
    CREATE: 'Created',
    UPDATE: 'Updated',
    DELETE: 'Deleted',
    CLOSE: 'Closed',
    OPEN: 'Opened',
    POST: 'Posted',
    SUBMIT: 'Submitted',
    APPROVE: 'Approved',
    REJECT: 'Rejected',
    RETURN: 'Returned',
    UPLOAD: 'Uploaded',
  };
  return map[action] || action.charAt(0) + action.slice(1).toLowerCase();
}

function auditActionDot(action: string) {
  switch (action) {
    case 'CLOSE':
    case 'APPROVE':
    case 'POST':
      return 'bg-emerald-500';
    case 'DELETE':
    case 'REJECT':
      return 'bg-red-500';
    case 'SUBMIT':
    case 'RETURN':
    case 'OPEN':
      return 'bg-amber-500';
    case 'UPLOAD':
    case 'CREATE':
      return 'bg-sky-500';
    default:
      return 'bg-slate-400';
  }
}

function auditActionBadge(
  action: string,
): 'default' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' {
  switch (action) {
    case 'CLOSE':
    case 'APPROVE':
    case 'POST':
      return 'success';
    case 'DELETE':
    case 'REJECT':
      return 'danger';
    case 'SUBMIT':
    case 'RETURN':
    case 'OPEN':
      return 'warning';
    case 'UPLOAD':
    case 'CREATE':
      return 'info';
    default:
      return 'secondary';
  }
}

const formatGeneratedAt = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const readinessVariant = (readiness: MgaAiOverviewResponse['overview']['readiness']) => {
  switch (readiness) {
    case 'READY':
      return 'success' as const;
    case 'CONDITIONAL':
      return 'warning' as const;
    case 'NOT_READY':
      return 'danger' as const;
    default:
      return 'secondary' as const;
  }
};

type MetricTone = 'default' | 'danger' | 'warning' | 'ok';

type TopMetric = {
  id: string;
  label: string;
  value: string;
  hint?: string;
  tone?: MetricTone;
};

const toneValueClass = (tone: MetricTone, isDark: boolean): string => {
  if (tone === 'danger') return isDark ? 'text-red-400' : 'text-red-600';
  if (tone === 'warning') return isDark ? 'text-amber-400' : 'text-amber-600';
  if (tone === 'ok') return isDark ? 'text-emerald-400' : 'text-emerald-700';
  return '';
};

const MetricCell: React.FC<Omit<TopMetric, 'id'>> = ({ label, value, hint, tone = 'default' }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="min-w-0 px-3 py-3 sm:px-4">
      <p className={cn('text-xs font-semibold uppercase tracking-wide leading-snug', colors.textMuted)}>
        {label}
      </p>
      <p
        className={cn(
          'mt-1.5 text-sm font-semibold tabular-nums truncate',
          tone === 'default' ? colors.text : toneValueClass(tone, isDark),
        )}
        title={value}
      >
        {value}
      </p>
      {hint ? (
        <p className={cn('mt-0.5 text-xs truncate', colors.textMuted)} title={hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
};

const MetricsGroup: React.FC<{
  title: string;
  metrics: TopMetric[];
  columnsClass?: string;
}> = ({ title, metrics, columnsClass = 'grid-cols-2 md:grid-cols-4' }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('rounded-xl border shadow-sm overflow-hidden', colors.surface, colors.border)}>
      <div className={cn('border-b px-4 py-2', colors.border, colors.headerBg)}>
        <h3 className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>{title}</h3>
      </div>
      <div className={cn('grid divide-x divide-y xl:divide-y-0', columnsClass, colors.border)}>
        {metrics.map(({ id, label, value, hint, tone }) => (
          <MetricCell key={id} label={label} value={value} hint={hint} tone={tone} />
        ))}
      </div>
    </section>
  );
};

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Tighter header/body padding (e.g. Files). */
  dense?: boolean;
}> = ({ title, subtitle, action, children, dense = false }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('overflow-hidden rounded-xl border shadow-sm', colors.surface, colors.border)}>
      <div
        className={cn(
          'flex items-center justify-between gap-3 border-b',
          dense ? 'px-4 py-2' : 'px-5 py-3',
          colors.border,
          colors.headerBg,
        )}
      >
        <div className="min-w-0">
          <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
          {subtitle && <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className={dense ? 'p-0' : 'p-5'}>{children}</div>
    </section>
  );
};

const AiOverviewBody: React.FC<{ data: MgaAiOverviewResponse; compactMeta?: boolean }> = ({
  data,
  compactMeta = false,
}) => {
  const { colors } = useTheme();
  const { overview } = data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-2 justify-between">
        <div className="min-w-0">
          <p className={cn('text-sm font-semibold', colors.text)}>{overview.headline}</p>
          <p className={cn('mt-1.5 text-sm leading-relaxed', colors.textSecondary)}>{overview.summary}</p>
        </div>
        <Badge variant={readinessVariant(overview.readiness)}>{overview.readiness.replace(/_/g, ' ')}</Badge>
      </div>
      <div className="grid gap-4 md:grid-cols-3">
        {(
          [
            ['Strengths', overview.strengths],
            ['Risks', overview.risks],
            ['Recommendations', overview.recommendations],
          ] as const
        ).map(([title, items]) => (
          <div key={title}>
            <p className={cn('text-xs font-semibold uppercase tracking-wide mb-2', colors.textMuted)}>{title}</p>
            {items.length ? (
              <ul className={cn('space-y-1.5 text-sm list-disc pl-4', colors.text)}>
                {items.map((itemText) => (
                  <li key={itemText}>{itemText}</li>
                ))}
              </ul>
            ) : (
              <p className={cn('text-sm', colors.textMuted)}>—</p>
            )}
          </div>
        ))}
      </div>
      {!compactMeta ? (
        <p className={cn('text-xs', colors.textMuted)}>
          Generated {formatGeneratedAt(data.generatedAt)} · {data.model}
        </p>
      ) : null}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const { colors, theme } = useTheme();
  const { can } = useAuth();
  const { showError, showSuccess } = useNotification();
  const { id } = useParams<{ id: string }>();
  const periodId = id ? parseInt(id, 10) : NaN;
  const { item, isLoading, mutate } = useMgaPeriod(Number.isInteger(periodId) ? periodId : 0);
  const { data: cash } = useMgaCashEntries(Number.isInteger(periodId) ? periodId : 0);
  const { data: bonded } = useMgaBondedLines(Number.isInteger(periodId) ? periodId : 0);
  const { data: requests } = useMgaCrewRequests(Number.isInteger(periodId) ? periodId : 0);
  const { data: auditEvents } = useMgaAuditEvents(Number.isInteger(periodId) ? periodId : 0);
  const journalId = item?.accDocument?.id ?? item?.accDocumentId ?? 0;
  const {
    item: journalDoc,
    isLoading: journalLoading,
  } = useAccDocument(Number.isInteger(journalId) && journalId > 0 ? journalId : 0);
  const canEdit =
    can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_mga', 'edit');

  const [aiOverview, setAiOverview] = useState<MgaAiOverviewResponse | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiHydrating, setAiHydrating] = useState(Number.isInteger(periodId));
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [journalPanelOpen, setJournalPanelOpen] = useState(false);
  const [auditPanelOpen, setAuditPanelOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<MgaAiOverviewResponse[]>([]);
  const [historySelectedId, setHistorySelectedId] = useState<number | null>(null);
  const [printOpen, setPrintOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!printOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (printRef.current?.contains(t)) return;
      setPrintOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [printOpen]);

  useEffect(() => {
    if (!Number.isInteger(periodId) || periodId <= 0) {
      setAiOverview(null);
      setAiHydrating(false);
      return;
    }
    let cancelled = false;
    setAiOverview(null);
    setAiHydrating(true);
    getMgaAiOverview(periodId)
      .then((saved) => {
        if (!cancelled) {
          setAiOverview(saved);
        }
      })
      .catch(() => {
        if (!cancelled) setAiOverview(null);
      })
      .finally(() => {
        if (!cancelled) setAiHydrating(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodId]);

  const summary = useMemo(() => {
    const lines = item?.lines ?? [];
    const ccy = item?.currency || 'USD';
    const cashBal = cash.reduce((s, e) => {
      const amt = Number(e.amountPeriod ?? e.amount) || 0;
      return e.entryType === 'EXPENSE' ? s - amt : s + amt;
    }, 0);
    const cashIn = cash
      .filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const cashOut = cash
      .filter((e) => e.entryType === 'EXPENSE')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const ctm = cash
      .filter((e) => e.entryType === 'CTM')
      .reduce((s, e) => s + (Number(e.amountPeriod ?? e.amount) || 0), 0);
    const cashInCount = cash.filter((e) => e.entryType === 'INCOME' || e.entryType === 'CTM').length;
    const cashOutCount = cash.filter((e) => e.entryType === 'EXPENSE').length;
    const bondedIn = bonded
      .filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const bondedOut = bonded
      .filter((l) => l.section === 'EXPENSE')
      .reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const bondedInCount = bonded.filter((l) => l.section === 'STOCK' || l.section === 'INCOMING')
      .length;
    const bondedOutCount = bonded.filter((l) => l.section === 'EXPENSE').length;
    const bondedPayrollLinked = bonded.filter(
      (l) => l.section === 'EXPENSE' && l.payrollColumnCode && l.mgaCrewLineId,
    ).length;

    const byCode = (code: string) =>
      requests.filter((r) => requestColumnCode(r) === code);
    const sumCode = (code: string) =>
      byCode(code).reduce((s, r) => s + (Number(r.amount) || 0), 0);

    const cashByMaster = sumCode('requestedCashByMaster');
    const officeAdv = sumCode('cashAdvanceOffice');
    const allotments = sumCode('allotmentAmount');
    const cig = sumCode('cigarettesAmount');
    const internet = sumCode('internetCardsAmount');
    const otherBonded = sumCode('otherBondedAmount');
    const crewBondedTotal = cig + internet + otherBonded;
    const crewBondedCount =
      byCode('cigarettesAmount').length +
      byCode('internetCardsAmount').length +
      byCode('otherBondedAmount').length;

    const missingWageSplit = lines.filter((l) => l.missingWageSplit).length;
    return {
      lines,
      ccy,
      cashBal,
      cashIn,
      cashOut,
      ctm,
      cashInCount,
      cashOutCount,
      bondedIn,
      bondedOut,
      bondedInCount,
      bondedOutCount,
      bondedBal: bondedIn - bondedOut,
      bondedPayrollLinked,
      cashByMaster,
      officeAdv,
      allotments,
      cig,
      internet,
      otherBonded,
      crewBondedTotal,
      crewBondedCount,
      cashReqCount: byCode('requestedCashByMaster').length,
      officeAdvCount: byCode('cashAdvanceOffice').length,
      allotReqCount: byCode('allotmentAmount').length,
      missingWageSplit,
    };
  }, [item, cash, bonded, requests]);

  const glanceMetrics = useMemo((): TopMetric[] => {
    if (!item) return [];
    return [
      {
        id: 'crew',
        label: 'Crew',
        value: String(summary.lines.length),
        hint: 'Payroll lines',
      },
      {
        id: 'net',
        label: 'Net due',
        value: formatAmt(item.totals?.netDue, summary.ccy),
        hint: `Gross ${formatAmt(item.totals?.grossPayable, summary.ccy)}`,
        tone: Number(item.totals?.netDue) < 0 ? 'danger' : 'default',
      },
      {
        id: 'ot',
        label: 'OT total',
        value: formatAmt(item.totals?.otTotal, summary.ccy),
      },
      {
        id: 'cash',
        label: 'Cash on hand',
        value: formatAmt(summary.cashBal, summary.ccy),
        hint: `Income/CTM ${formatAmt(summary.cashIn, summary.ccy)} · Out ${formatAmt(summary.cashOut, summary.ccy)}`,
        tone: summary.cashBal < 0 ? 'warning' : 'default',
      },
      {
        id: 'bonded',
        label: 'Bonded store',
        value: formatAmt(summary.bondedBal, summary.ccy),
        hint: `In ${summary.bondedInCount} · Stock out ${summary.bondedOutCount}`,
      },
      {
        id: 'crewBonded',
        label: 'Crew bonded',
        value: formatAmt(summary.crewBondedTotal, summary.ccy),
        hint: `${summary.crewBondedCount} charges · ${summary.bondedPayrollLinked} from Stock out`,
      },
      {
        id: 'allot',
        label: 'Allotments',
        value: formatAmt(summary.allotments, summary.ccy),
        hint: `${summary.allotReqCount} lines`,
      },
      {
        id: 'advances',
        label: 'Advances',
        value: formatAmt(summary.cashByMaster + summary.officeAdv, summary.ccy),
        hint: `Master ${summary.cashReqCount} · Office ${summary.officeAdvCount}`,
      },
      {
        id: 'wageGaps',
        label: 'Wage split gaps',
        value: String(summary.missingWageSplit),
        hint: summary.missingWageSplit ? 'Needs attention' : 'All set',
        tone: summary.missingWageSplit > 0 ? 'danger' : 'ok',
      },
    ];
  }, [item, summary]);

  const handleGenerateAiOverview = async () => {
    if (!Number.isInteger(periodId) || periodId <= 0) return;
    setAiLoading(true);
    try {
      const result = await generateMgaAiOverview(periodId);
      setAiOverview(result);
      setAiPanelOpen(true);
      if (historyOpen) {
        setHistoryItems((prev) => [result, ...prev.filter((row) => row.id !== result.id)]);
        setHistorySelectedId(result.id);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not generate AI overview.');
    } finally {
      setAiLoading(false);
    }
  };

  const openHistoryModal = async () => {
    if (!Number.isInteger(periodId) || periodId <= 0) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const rows = await getMgaAiOverviewHistory(periodId);
      setHistoryItems(rows);
      setHistorySelectedId(rows[0]?.id ?? aiOverview?.id ?? null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      showError(err?.response?.data?.message || err?.message || 'Could not load AI overview history.');
      setHistoryItems([]);
      setHistorySelectedId(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    setHistoryOpen(false);
    setHistoryLoading(false);
  };

  const selectedHistory = historyItems.find((row) => row.id === historySelectedId) ?? null;

  if (!Number.isInteger(periodId)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className={cn(colors.primaryFg, 'underline')}>
          Back to MGA
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>MGA period not found</h1>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('mt-4 inline-block text-sm underline', colors.primaryFg)}
        >
          Back to MGA
        </Link>
      </div>
    );
  }

  const sheet = moduleConfig.module.route.sheet(item.id);
  const vesselName = item.vessel?.vesselName ?? `Vessel #${item.vesselId}`;

  const ledgerGroups: Array<{
    id: string;
    title: string;
    rows: Array<{ label: string; value: string; hint?: string }>;
  }> = [
    {
      id: 'payroll',
      title: 'Payroll',
      rows: [
        { label: 'Gross', value: formatAmt(item.totals?.grossPayable, summary.ccy) },
        { label: 'OT total', value: formatAmt(item.totals?.otTotal, summary.ccy) },
        {
          label: 'Net due',
          value: formatAmt(item.totals?.netDue, summary.ccy),
          hint: `${summary.lines.length} crew`,
        },
      ],
    },
    {
      id: 'cash',
      title: 'Cash / CTM',
      rows: [
        {
          label: 'Income / CTM',
          value: formatAmt(summary.cashIn, summary.ccy),
          hint: `${summary.cashInCount} · CTM ${formatAmt(summary.ctm, summary.ccy)}`,
        },
        {
          label: 'Cash out',
          value: formatAmt(summary.cashOut, summary.ccy),
          hint: `${summary.cashOutCount} lines`,
        },
        { label: 'Cash on hand', value: formatAmt(summary.cashBal, summary.ccy) },
      ],
    },
    {
      id: 'bonded',
      title: 'Bonded store',
      rows: [
        {
          label: 'Incoming stock',
          value: formatAmt(summary.bondedIn, summary.ccy),
          hint: `${summary.bondedInCount} lines`,
        },
        {
          label: 'Stock out',
          value: formatAmt(summary.bondedOut, summary.ccy),
          hint: `${summary.bondedOutCount} · ${summary.bondedPayrollLinked} on payroll`,
        },
        { label: 'Store balance', value: formatAmt(summary.bondedBal, summary.ccy) },
        {
          label: 'Crew charges',
          value: formatAmt(summary.crewBondedTotal, summary.ccy),
          hint: `${summary.crewBondedCount} lines`,
        },
      ],
    },
    {
      id: 'settlement',
      title: 'Advances & allotments',
      rows: [
        {
          label: 'Cash by master',
          value: formatAmt(summary.cashByMaster, summary.ccy),
          hint: `${summary.cashReqCount} lines`,
        },
        {
          label: 'Office advances',
          value: formatAmt(summary.officeAdv, summary.ccy),
          hint: `${summary.officeAdvCount} lines`,
        },
        {
          label: 'Allotments',
          value: formatAmt(summary.allotments, summary.ccy),
          hint: `${summary.allotReqCount} lines`,
        },
      ],
    },
  ];

  const broughtForward = carryForwardSummary({
    previousBalance: item.totals?.previousBalance,
    cash,
    bonded,
  });

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="w-full space-y-4 px-4 py-6 sm:px-6 lg:px-8 pb-8">
        <Link
          to={moduleConfig.module.route.list}
          className={cn(
            'inline-flex items-center gap-1 text-sm font-medium',
            colors.textSecondary,
            'hover:underline',
          )}
        >
          <ChevronLeftIcon className="h-4 w-4 opacity-80" />
          MGA
        </Link>

        {/* Identity */}
        <section className={cn('relative z-20 rounded-xl border shadow-sm', colors.surface, colors.border)}>
          <div className="flex items-start gap-3 px-4 py-3 sm:gap-4 sm:px-5">
            <div
              className={cn(
                'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[11px] font-semibold ring-1 ring-inset',
                colors.surfaceHover,
                colors.text,
                colors.border,
              )}
              aria-hidden
            >
              MGA
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                <div className="min-w-0">
                  <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <h1 className={cn('truncate text-lg font-semibold sm:text-xl', colors.text)}>
                      {vesselName}
                    </h1>
                    <Badge variant={statusBadgeVariant(item.status)} className="text-[10px]">
                      {formatMgaStatus(item.status)}
                    </Badge>
                    {item.closeApproval ? (
                      <Badge
                        variant={closeApprovalBadgeVariant(item.closeApproval.status)}
                        className="text-[10px]"
                      >
                        Close:{' '}
                        {item.closeApproval.status === 'DRAFT'
                          ? 'Returned'
                          : item.closeApproval.status.charAt(0) +
                            item.closeApproval.status.slice(1).toLowerCase()}
                      </Badge>
                    ) : null}
                    <span
                      className={cn(
                        'inline-flex rounded-md px-1.5 py-0.5 font-mono text-[11px]',
                        colors.surfaceHover,
                        colors.textSecondary,
                      )}
                    >
                      #{item.id}
                    </span>
                    {summary.missingWageSplit > 0 ? (
                      <Badge variant="danger" className="text-[10px]">
                        {summary.missingWageSplit} wage split gap
                      </Badge>
                    ) : null}
                    {item.accDocument ? (
                      <Badge variant="info" className="text-[10px]">
                        Journal linked
                      </Badge>
                    ) : null}
                  </div>
                  <p className={cn('mt-1 truncate text-xs sm:text-sm', colors.textMuted)}>
                    {[
                      formatDateGlobal(item.periodFrom) + ' → ' + formatDateGlobal(item.periodTo),
                      item.currency,
                      item.vessel?.vesselAbrv,
                      item.notes || null,
                      item.status === 'CLOSED' && item.masterApprovedByName
                        ? `Closed by ${item.masterApprovedByName}${
                            item.masterApprovedAt ? ` · ${formatDateGlobal(item.masterApprovedAt)}` : ''
                          }`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <div className="relative z-50" ref={printRef}>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      aria-haspopup="menu"
                      aria-expanded={printOpen}
                      onClick={() => setPrintOpen((v) => !v)}
                    >
                      <PrinterIcon className="h-4 w-4" />
                      Print
                      <ChevronDownIcon
                        className={cn('h-3.5 w-3.5 transition-transform', printOpen && 'rotate-180')}
                      />
                    </Button>
                    {printOpen ? (
                      <div
                        role="menu"
                        className={cn(
                          'absolute right-0 z-50 mt-1 min-w-[15rem] overflow-hidden rounded-md border py-1 shadow-lg',
                          colors.surface,
                          colors.border,
                        )}
                      >
                        {MGA_REPORT_KEYS.map((key) => (
                          <Link
                            key={key}
                            role="menuitem"
                            to={`/mga/${periodId}/reports/${key}`}
                            className={cn(
                              'flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/10',
                              colors.text,
                            )}
                            onClick={() => setPrintOpen(false)}
                          >
                            <span className="flex items-center gap-2 text-sm font-medium">
                              <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" />
                              {MGA_REPORT_META[key].title}
                            </span>
                            <span className={cn('pl-6 text-[11px] leading-snug', colors.textMuted)}>
                              {MGA_REPORT_META[key].subtitle}
                            </span>
                          </Link>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {canEdit ? (
                    <Link to={sheet} className="shrink-0">
                      <Button type="button" variant="accent" size="sm">
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>

        <CloseApprovalStatusCard period={item} />

        {/* AI overview */}
        <AccordionItem
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn('text-sm font-semibold', colors.text)}>AI overview</span>
              {aiOverview ? (
                <Badge variant={readinessVariant(aiOverview.overview.readiness)}>
                  {aiOverview.overview.readiness.replace(/_/g, ' ')}
                </Badge>
              ) : null}
            </span>
          }
          description="Close readiness from payroll, Books (cash, bonded, advances), and wage splits"
          open={aiPanelOpen}
          onToggle={setAiPanelOpen}
          headerAction={
            <div className="flex flex-wrap items-center gap-2 pr-1">
              <Button type="button" variant="outline" size="sm" onClick={openHistoryModal}>
                History
              </Button>
              <Button
                type="button"
                variant={aiOverview ? 'outline' : 'accent'}
                size="sm"
                loading={aiLoading}
                onClick={handleGenerateAiOverview}
              >
                {aiOverview ? 'Refresh' : 'Generate'}
              </Button>
            </div>
          }
          className={cn(colors.surface, 'shadow-sm rounded-xl')}
        >
          {aiHydrating && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading saved overview…</p>
          ) : aiLoading && !aiOverview ? (
            <p className={cn('text-sm', colors.textMuted)}>Analyzing MGA period…</p>
          ) : aiOverview ? (
            <AiOverviewBody data={aiOverview} />
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>
              Generate an AI overview from payroll totals, Books categories (cash / CTM, bonded store, allotments), and wage-split flags.
            </p>
          )}
        </AccordionItem>

        <MetricsGroup
          title="At a glance"
          metrics={glanceMetrics}
          columnsClass="grid-cols-2 md:grid-cols-3 xl:grid-cols-9"
        />

        {broughtForward.hasAny ? (
          <Panel
            title="Brought forward"
            subtitle="Opening figures imported from the prior closed period"
          >
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Previous balance
                </dt>
                <dd className={cn('mt-1 text-sm font-semibold tabular-nums', colors.text)}>
                  {formatAmt(broughtForward.previousBalance, summary.ccy)}
                </dd>
                <dd className={cn('text-xs', colors.textMuted)}>Crew payable carried in</dd>
              </div>
              <div>
                <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Opening cash
                </dt>
                <dd className={cn('mt-1 text-sm font-semibold tabular-nums', colors.text)}>
                  {formatAmt(broughtForward.openingCash, summary.ccy)}
                </dd>
                <dd className={cn('text-xs', colors.textMuted)}>
                  <Link
                    to={`${sheet}?tab=books&category=${encodeURIComponent(CASH_INCOME_CODE)}`}
                    className={cn('underline', colors.primaryFg)}
                  >
                    Income / CTM
                  </Link>
                </dd>
              </div>
              <div>
                <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>
                  Opening bonded
                </dt>
                <dd className={cn('mt-1 text-sm font-semibold tabular-nums', colors.text)}>
                  {formatAmt(broughtForward.openingBonded, summary.ccy)}
                </dd>
                <dd className={cn('text-xs', colors.textMuted)}>
                  <Link
                    to={`${sheet}?tab=books&category=${encodeURIComponent(BONDED_INCOMING_CODE)}`}
                    className={cn('underline', colors.primaryFg)}
                  >
                    Incoming stock
                  </Link>
                </dd>
              </div>
            </dl>
          </Panel>
        ) : null}

        <Panel
          title="Account summary"
          subtitle="Payroll and Books totals for this period"
          action={
            <Link
              to={sheet}
              className={cn('text-xs font-medium underline-offset-2 hover:underline', colors.primaryFg)}
            >
              Open sheet →
            </Link>
          }
        >
          <div
            className={cn(
              'grid gap-0 overflow-hidden rounded-xl border sm:grid-cols-2 xl:grid-cols-4',
              colors.border,
            )}
          >
            {ledgerGroups.map((group, groupIndex) => (
              <div
                key={group.id}
                className={cn(
                  'min-w-0 p-4',
                  groupIndex > 0 && 'border-t sm:border-t-0',
                  groupIndex % 2 === 1 && 'sm:border-l',
                  groupIndex >= 2 && 'xl:border-t-0',
                  groupIndex > 0 && 'xl:border-l',
                  colors.border,
                )}
              >
                <p className={cn('mb-3 text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                  {group.title}
                </p>
                <dl className="space-y-2.5">
                  {group.rows.map((row) => (
                    <div key={row.label} className="min-w-0">
                      <div className="flex items-baseline justify-between gap-3">
                        <dt className={cn('truncate text-sm', colors.textSecondary)}>{row.label}</dt>
                        <dd
                          className={cn('shrink-0 text-sm font-semibold tabular-nums', colors.text)}
                          title={row.value}
                        >
                          {row.value}
                        </dd>
                      </div>
                      {row.hint ? (
                        <p className={cn('mt-0.5 truncate text-[11px]', colors.textMuted)} title={row.hint}>
                          {row.hint}
                        </p>
                      ) : null}
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
          <div
            className={cn(
              'mt-4 flex flex-wrap items-end justify-between gap-3 rounded-xl border px-4 py-3.5',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <div className="min-w-0">
              <p className={cn('text-[11px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                Balance due to crew
              </p>
              <p className={cn('mt-0.5 text-xs', colors.textMuted)}>
                Payroll net after Books-linked charges and advances
              </p>
            </div>
            <p className={cn('text-2xl font-semibold tabular-nums', colors.text)}>
              {formatAmt(item.totals?.netDue, summary.ccy)}
            </p>
          </div>
        </Panel>

        <Panel
          dense
          title={
            (item.attachments?.length ?? 0) > 0
              ? `Files · ${item.attachments!.length}`
              : 'Files'
          }
          subtitle="Supporting documents — upload from the sheet Files tab"
          action={
            <Link
              to={`${sheet}?tab=files`}
              className={cn('text-xs font-medium underline-offset-2 hover:underline', colors.primaryFg)}
            >
              Open Files →
            </Link>
          }
        >
          <PeriodFilesPanel
            periodId={periodId}
            files={item.attachments ?? []}
            canEdit={false}
            canDelete={false}
            onChanged={() => mutate()}
            layout="panel"
          />
        </Panel>

        <AccordionItem
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn('text-sm font-semibold', colors.text)}>Finance journal</span>
              {item.accDocument ? (
                <Badge
                  variant={
                    item.accDocument.status === 'SAVED'
                      ? 'success'
                      : item.accDocument.status === 'TEMP'
                        ? 'warning'
                        : 'secondary'
                  }
                  className="text-[10px]"
                >
                  {item.accDocument.code || `#${item.accDocument.id}`}
                  {' · '}
                  {item.accDocument.status === 'DRAFT'
                    ? 'Draft'
                    : item.accDocument.status === 'TEMP'
                      ? 'Temp'
                      : item.accDocument.status === 'SAVED'
                        ? 'Saved'
                        : item.accDocument.status}
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-[10px]">
                  None
                </Badge>
              )}
            </span>
          }
          description="Created when the period is closed (if posting is configured)"
          open={journalPanelOpen}
          onToggle={setJournalPanelOpen}
          headerAction={
            item.accDocument ? (
              <Link to={`/accounting/journals/${item.accDocument.id}`}>
                <Button type="button" variant="outline" size="sm" className="gap-1.5">
                  Open
                </Button>
              </Link>
            ) : undefined
          }
          className={cn(colors.surface, 'shadow-sm rounded-xl')}
        >
          {item.accDocument ? (
            <div className={cn('-mx-1 overflow-hidden rounded-xl border', colors.border)}>
              <div
                className={cn(
                  'grid gap-0 border-b sm:grid-cols-2 lg:grid-cols-4',
                  colors.border,
                )}
              >
                {(
                  [
                    {
                      label: 'Document',
                      value: item.accDocument.code || `#${item.accDocument.id}`,
                      hint: journalDoc?.documentType?.name || journalDoc?.documentType?.code || null,
                    },
                    {
                      label: 'Status',
                      value:
                        item.accDocument.status === 'DRAFT'
                          ? 'Draft'
                          : item.accDocument.status === 'TEMP'
                            ? 'Temp'
                            : item.accDocument.status === 'SAVED'
                              ? 'Saved'
                              : item.accDocument.status,
                      hint: null,
                    },
                    {
                      label: 'Posting date',
                      value: item.accDocument.postingDate
                        ? formatDateGlobal(item.accDocument.postingDate)
                        : journalDoc?.documentDate
                          ? formatDateGlobal(journalDoc.documentDate)
                          : '—',
                      hint: journalDoc?.documentDate
                        ? `Doc ${formatDateGlobal(journalDoc.documentDate)}`
                        : null,
                    },
                    {
                      label: 'Lines',
                      value: journalLoading
                        ? '…'
                        : String(journalDoc?.lines?.length ?? journalDoc?._count?.lines ?? '—'),
                      hint:
                        journalDoc?.lineTotals != null
                          ? journalDoc.lineTotals.isBalanced === false
                            ? 'Out of balance'
                            : 'Balanced'
                          : null,
                    },
                  ] as const
                ).map((cell) => (
                  <div
                    key={cell.label}
                    className={cn(
                      'min-w-0 border-t px-4 py-2.5 sm:border-t-0 sm:border-l first:border-l-0',
                      colors.border,
                    )}
                  >
                    <p className={cn('text-[10px] font-semibold uppercase tracking-wide', colors.textMuted)}>
                      {cell.label}
                    </p>
                    <p className={cn('mt-1 truncate text-sm font-semibold', colors.text)} title={cell.value}>
                      {cell.value}
                    </p>
                    {cell.hint ? (
                      <p className={cn('mt-0.5 truncate text-[11px]', colors.textMuted)}>{cell.hint}</p>
                    ) : null}
                  </div>
                ))}
              </div>

              {item.accDocument.description || journalDoc?.description ? (
                <p className={cn('border-b px-4 py-2 text-sm', colors.border, colors.textSecondary)}>
                  {item.accDocument.description || journalDoc?.description}
                </p>
              ) : null}

              {journalDoc?.lineTotals ? (
                <div
                  className={cn(
                    'flex flex-wrap items-center gap-x-4 gap-y-1 border-b px-4 py-2 text-xs tabular-nums',
                    colors.border,
                    colors.textMuted,
                  )}
                >
                  <span>
                    Debit{' '}
                    <span className={cn('font-semibold', colors.text)}>
                      {Number(journalDoc.lineTotals.debitLocal).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                  <span>
                    Credit{' '}
                    <span className={cn('font-semibold', colors.text)}>
                      {Number(journalDoc.lineTotals.creditLocal).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </span>
                  {journalDoc.companyCodes?.length ? (
                    <span>Companies {journalDoc.companyCodes.join(', ')}</span>
                  ) : null}
                  <Badge
                    variant={journalDoc.lineTotals.isBalanced === false ? 'warning' : 'success'}
                    className="text-[10px]"
                  >
                    {journalDoc.lineTotals.isBalanced === false ? 'Out of balance' : 'Balanced'}
                  </Badge>
                </div>
              ) : null}

              <div className="max-h-[22rem] overflow-auto">
                {journalLoading ? (
                  <p className={cn('px-4 py-3 text-sm', colors.textMuted)}>Loading journal lines…</p>
                ) : journalDoc?.lines?.length ? (
                  <DocumentLinesView lines={journalDoc.lines} embedded />
                ) : (
                  <p className={cn('px-4 py-3 text-sm', colors.textMuted)}>
                    No lines on this journal yet.
                  </p>
                )}
              </div>
            </div>
          ) : item.status === 'CLOSED' ? (
            <p className={cn('text-sm', colors.textMuted)}>
              No AccDocument linked. Configure mga_posting_config (wages expense + crew payable) to
              post on close.
            </p>
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>
              Close the period to create a draft AccDocument journal when posting is enabled.
            </p>
          )}
        </AccordionItem>

        <AccordionItem
          title={
            <span className="flex flex-wrap items-center gap-2">
              <span className={cn('text-sm font-semibold', colors.text)}>Change history</span>
              {auditEvents.length > 0 ? (
                <Badge variant="secondary" className="text-[10px]">
                  {Math.min(auditEvents.length, 40)}
                </Badge>
              ) : null}
            </span>
          }
          description="Recent edits on this period"
          open={auditPanelOpen}
          onToggle={setAuditPanelOpen}
          className={cn(colors.surface, 'shadow-sm rounded-xl')}
        >
          {auditEvents.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No audit events yet.</p>
          ) : (
            <ol className={cn('max-h-72 overflow-y-auto rounded-xl border', colors.border)}>
              {auditEvents.slice(0, 40).map((ev, index, list) => {
                const changes = ev.changes as
                  | { fileName?: string; kind?: string; files?: Array<{ fileName: string }> }
                  | null
                  | undefined;
                const fileHint =
                  changes?.fileName ||
                  changes?.files?.map((f) => f.fileName).filter(Boolean).join(', ') ||
                  null;
                const entity = auditEntityLabel(ev.entityType, ev.summary, ev.action);
                const when = formatDateTime(ev.createdAt) || formatDateGlobal(ev.createdAt);
                return (
                  <li
                    key={ev.id}
                    className={cn(
                      'relative flex gap-3 px-4 py-2.5',
                      index < list.length - 1 && 'border-b',
                      colors.border,
                    )}
                  >
                    <div className="relative flex w-3 shrink-0 justify-center pt-1.5">
                      {index < list.length - 1 ? (
                        <span
                          className={cn(
                            'absolute top-3 bottom-[-0.65rem] w-px',
                            theme === 'dark' ? 'bg-slate-700' : 'bg-slate-200',
                          )}
                          aria-hidden
                        />
                      ) : null}
                      <span
                        className={cn(
                          'relative z-[1] h-2.5 w-2.5 rounded-full ring-2',
                          auditActionDot(ev.action),
                          theme === 'dark' ? 'ring-slate-900' : 'ring-white',
                        )}
                        aria-hidden
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                        <p className={cn('min-w-0 flex-1 text-sm font-medium leading-snug', colors.text)}>
                          {ev.summary}
                        </p>
                        <time
                          className={cn('shrink-0 text-[11px] tabular-nums', colors.textMuted)}
                          dateTime={ev.createdAt}
                        >
                          {when}
                        </time>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <Badge variant="secondary" className="text-[10px]">
                          {entity}
                        </Badge>
                        <Badge variant={auditActionBadge(ev.action)} className="text-[10px]">
                          {auditActionLabel(ev.action)}
                        </Badge>
                        {ev.userName ? (
                          <span className={cn('text-[11px]', colors.textMuted)}>{ev.userName}</span>
                        ) : null}
                        {fileHint && !ev.summary.includes(fileHint) ? (
                          <span className={cn('truncate text-[11px]', colors.textMuted)} title={fileHint}>
                            {fileHint}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </AccordionItem>
      </div>

      <Modal isOpen={historyOpen} onClose={closeHistoryModal} size="2xl">
        <ModalHeader>
          <ModalTitle>AI overview history</ModalTitle>
        </ModalHeader>
        <ModalContent className={colors.headerBg}>
          {historyLoading ? (
            <p className={cn('text-sm', colors.textMuted)}>Loading history…</p>
          ) : historyItems.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No saved overviews yet.</p>
          ) : (
            <div className="grid gap-4 md:grid-cols-[minmax(12rem,16rem)_1fr] min-h-[20rem]">
              <div className={cn('rounded-lg border overflow-hidden', colors.border, colors.surface)}>
                <ul className={cn('max-h-[28rem] overflow-y-auto divide-y', colors.divide)}>
                  {historyItems.map((row) => {
                    const active = row.id === historySelectedId;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          onClick={() => setHistorySelectedId(row.id)}
                          className={cn(
                            'w-full text-left px-3 py-2.5 transition-colors',
                            active ? colors.surfaceHover : colors.interactiveHover,
                          )}
                        >
                          <p className={cn('text-xs font-medium', colors.textMuted)}>
                            {formatGeneratedAt(row.generatedAt)}
                          </p>
                          <p className={cn('mt-0.5 text-sm font-medium truncate', colors.text)}>
                            {row.overview.headline || 'Overview'}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            <Badge variant={readinessVariant(row.overview.readiness)}>
                              {row.overview.readiness.replace(/_/g, ' ')}
                            </Badge>
                            {aiOverview?.id === row.id ? <Badge variant="info">Latest</Badge> : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className={cn('rounded-lg border p-4', colors.border, colors.surface)}>
                {selectedHistory ? (
                  <div className="space-y-3">
                    <p className={cn('text-xs', colors.textMuted)}>
                      {formatGeneratedAt(selectedHistory.generatedAt)} · {selectedHistory.model}
                    </p>
                    <AiOverviewBody data={selectedHistory} compactMeta />
                  </div>
                ) : (
                  <p className={cn('text-sm', colors.textMuted)}>Select a generation to view.</p>
                )}
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={closeHistoryModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Dashboard;
